import { formatDate } from "@angular/common";
import { Component } from "@angular/core";
import { map, Observable, of } from "rxjs";
import { camelCase, chain, startCase } from "lodash";

import {
	BaiscFilterSection,
	BaseComponent,
	CellData,
	Filter,
	FilterChangeModel,
	FilterComparisonOperator,
	FilterDescriptor,
	FilterOperator,
	FilterType,
	FilterValue,
	GridEndpoints,
	LogService,
	PagedQueryParameter,
	ReportColumn,
	ReportColumnData,
	ReportColumnSortDirection,
	ReportSettingsColumnDto,
	SortDescriptor,
	SortDirection
} from "@nstep-common/core";

@Component({
	template: '',
	standalone: false
})
export class ReportBaseComponent extends BaseComponent {

	SortDirection = SortDirection;
	FilterTypeEnum = FilterType;
	BaiscFilterSection = BaiscFilterSection;
	FilterComparisonOperatorEnum = FilterComparisonOperator;

	agregateOperators = [FilterOperator.AND, FilterOperator.OR];

	reportTemplateColumns: { [key: number]: ReportColumn[] } = {};
	filtersWithoutValue = [FilterComparisonOperator.IsEmpty, FilterComparisonOperator.IsNotEmpty, FilterComparisonOperator.IsNull, FilterComparisonOperator.IsNotNull];

	constructor(protected logger: LogService) {
		super();
	}

	getColumnFilterCollection(endpoint: string, gridService: any): Observable<string[]> {
		switch (endpoint) {
			case GridEndpoints.Headquarters:
				return gridService.getHeadquarters()
					.pipe(map((response: string[]) => response));
			case GridEndpoints.EntitlementCodes:
				return gridService.getCodes()
					.pipe(map((response: string[]) => response));
			case GridEndpoints.EntitlementTypes:
				return gridService.getTypes()
					.pipe(map((response: string[]) => response));
			case GridEndpoints.EntitlementUnits:
				return gridService.getUnits()
					.pipe(map((response: string[]) => response));
			case GridEndpoints.PosLocations:
				return gridService.getLocations()
					.pipe(map((response: string[]) => response));
			case GridEndpoints.TransactionStates:
				return gridService.getTransactionStates()
					.pipe(map((response: string[]) => response));
			case GridEndpoints.TransactionTypes:
				return gridService.getTransactionTypes()
					.pipe(map((response: string[]) => response));
			case GridEndpoints.RationedTypes:
				return gridService.getRationedTypes()
					.pipe(map((response: string[]) => response));
			case GridEndpoints.POSLocationNames:
				return gridService.getLocations()
					.pipe(map((response: string[]) => response));
			case GridEndpoints.Countries:
				return gridService.getCountries()
					.pipe(map((response: string[]) => response));
			case GridEndpoints.Cities:
				return gridService.getCities()
					.pipe(map((response: string[]) => response));
			default:
				return of([]);
		}
	}

	getDefaultFilters(filterType: FilterType, results: string[], isTimestampColumn: boolean = false): Filter[] {
		switch (filterType) {
			case FilterType.Basic:
				return [
					new Filter({ operator: isTimestampColumn ? FilterComparisonOperator.IsLessThan : FilterComparisonOperator.IsEqualTo, value: '' }),
					new Filter({ operator: isTimestampColumn ? FilterComparisonOperator.IsLessThan : FilterComparisonOperator.IsEqualTo, value: '' })
				];
			case FilterType.Bool:
				return [
					new Filter({ operator: FilterComparisonOperator.IsEqualTo, value: '', text: 'true' }),
					new Filter({ operator: FilterComparisonOperator.IsEqualTo, value: '', text: 'false' })
				];
			case FilterType.List:
				return results.map(r => new Filter({ operator: FilterComparisonOperator.IsEqualTo, value: '', text: r }));
		}
	}

	getColumnData(defaultTemplateColumns: ReportColumn[], reportSettingsColumns: ReportSettingsColumnDto[]): ReportColumnData {
		const colData = new ReportColumnData();

		colData.reportColumns = chain(reportSettingsColumns)
			.orderBy(c => c.order)
			.map(c => {
				const defaultColumnData = defaultTemplateColumns.find(d => d.key === camelCase(c.name))!;
				const availableColumnFilters = defaultColumnData.assignedFilters!.map(f => ({ ...f }));

				if (c.sortDirection) {
					const sortation = new SortDescriptor({
						columnName: c.name,
						sortDirection: c.sortDirection
					})

					colData.sortList.push(sortation);
				}

				const filter = new FilterDescriptor({
					columnName: c.name,
					agregateOperator: defaultColumnData.filterType === FilterType.Basic ? FilterOperator.AND : FilterOperator.OR,
					filterValue: c.filters.map(f => new FilterValue({
						operator: f.operator,
						value: f.value
					}))
				})

				if (filter.filterValue.length) colData.filterList.push(filter);

				const savedColumnFilters = c.filters.map(f => new Filter({
					operator: f.operator,
					value: f.value
				}));

				const assignedFilters = this.getAssignedFilter(availableColumnFilters, savedColumnFilters, defaultColumnData!.isDate, defaultColumnData!.filterType);

				const col = new ReportColumn({
					key: camelCase(c.name),
					name: startCase(c.name),
					orderNumber: c.order,
					isShown: c.show,
					isDate: defaultColumnData?.isDate,
					sortDirection: c.sortDirection,
					sortOrder: c.sortOrder,
					filterType: defaultColumnData.filterType,
					validOperators: defaultColumnData?.validOperators,
					agregateOperator: defaultColumnData.filterType === FilterType.Basic ? FilterOperator.AND : FilterOperator.OR,
					assignedFilters: assignedFilters,
					hasFilterApplied: assignedFilters.some(f => defaultColumnData.filterType === FilterType.Basic && (f.value || f.dateValue) || defaultColumnData.filterType !== FilterType.Basic && f.isChecked),
					selectedItemsCount: assignedFilters.filter(f => f.isChecked).length
				});

				return col;
			})
			.value();

		return colData;
	}

	private getAssignedFilter(availableColumnFilters: Filter[], savedColumnFilters: Filter[], isDate: boolean, filterType: FilterType): Filter[] {

		let result: Filter[] = [];

		switch (filterType) {
			case FilterType.Basic:
				result[0] = savedColumnFilters[0] ? new Filter({
					operator: savedColumnFilters[0].operator,
					value: !isDate ? savedColumnFilters[0].value : '',
					dateValue: isDate ? new Date(savedColumnFilters[0].value) : null
				}) : availableColumnFilters[0];

				result[1] = savedColumnFilters[1] ? new Filter({
					operator: savedColumnFilters[1].operator,
					value: !isDate ? savedColumnFilters[1].value : '',
					dateValue: isDate ? new Date(savedColumnFilters[1].value) : null
				}) : availableColumnFilters[1];

				break;
			default:
				result = availableColumnFilters.map(f => {
					const nonBasicSavedFilter = savedColumnFilters.find(s => s.value === f.text);

					return new Filter({
						text: f.text,
						value: nonBasicSavedFilter ? nonBasicSavedFilter.value : '',
						operator: nonBasicSavedFilter ? nonBasicSavedFilter.operator : FilterComparisonOperator.IsEqualTo,
						dateValue: nonBasicSavedFilter && isDate ? new Date(nonBasicSavedFilter!.value) : null,
						isChecked: nonBasicSavedFilter && nonBasicSavedFilter.value !== ''
					})
				});

				break;
		}

		return result;
	}

	getTemplatePaginatedData(data: any[]): any[][] {
		return data.map(item => {
			const row: CellData[] = [];

			Object.keys(item).forEach(key => {
				const value = item[key];
				row.push(new CellData({ key: key, value: key === 'timestamp' ? formatDate(value, 'dd/MM/yyyy - h:mm a', 'en') : Object.prototype.toString.call(value) === '[object Date]' ? formatDate(value, 'dd/MM/yyyy', 'en') : value }))
			});

			return row;
		})
	}

	processColumnSortation(column: ReportColumnSortDirection, reportColumns: ReportColumn[], reportPagedQueryModel: PagedQueryParameter): void {
		const formattedColumnName = column.name.replace(/\s/g, '');
		const currentSortation = reportPagedQueryModel.sortList.find(s => s.columnName === formattedColumnName);

		if (currentSortation) {
			reportPagedQueryModel.sortList.splice(reportPagedQueryModel.sortList.indexOf(currentSortation), 1);

			//remake sort order when user changes a column sortation
			reportPagedQueryModel.sortList.forEach((sortation, index) => {
				const reportColumn = reportColumns.find(r => r.name.replace(/\s/g, '') === sortation.columnName)!
				reportColumn.sortOrder = index + 1;
			});
		}

		const reportColumn = reportColumns.find(r => r.name === column.name)!
		reportColumn.sortDirection = column.sortDirection;
		reportColumn.sortOrder = null;

		if (column.sortDirection) {
			reportColumn.sortOrder = reportPagedQueryModel.sortList.length + 1;

			const sortation = new SortDescriptor({
				columnName: formattedColumnName,
				sortDirection: column.sortDirection
			})

			reportPagedQueryModel.sortList.push(sortation);
		}
	}

	proccessColumnFilters(filterChange: FilterChangeModel, pageQuery: PagedQueryParameter): void {
		if (filterChange.clearColumnFilter) {

			if (filterChange.col.filterType === FilterType.Basic) {
				filterChange.col.assignedFilters = [
					new Filter({ operator: FilterComparisonOperator.IsEqualTo, value: '' }),
					new Filter({ operator: FilterComparisonOperator.IsEqualTo, value: '' })
				];
			}

			if (filterChange.col.filterType === FilterType.List) {
				filterChange.col.assignedFilters.forEach(filter => {
					filter.isChecked = false;
					filter.value = '';
				});
			}

			if (filterChange.col.filterType === FilterType.Bool) {
				filterChange.col.assignedFilters = [
					new Filter({ operator: FilterComparisonOperator.IsEqualTo, value: '', text: 'true' }),
					new Filter({ operator: FilterComparisonOperator.IsEqualTo, value: '', text: 'false' })
				];
			}
		}

		pageQuery.page = 1;

		const formattedColumnName = filterChange.col.name.replace(/\s/g, '');

		let filterIndex = pageQuery.filterList.findIndex(d => d.columnName === formattedColumnName);

		if (filterIndex == -1) {
			pageQuery.filterList.push(new FilterDescriptor({
				columnName: formattedColumnName,
				agregateOperator: filterChange.col.agregateOperator!
			}));

			filterIndex = pageQuery.filterList.length - 1;
		} else {
			pageQuery.filterList[filterIndex].agregateOperator = filterChange.col.agregateOperator!
			pageQuery.filterList[filterIndex].columnName = formattedColumnName
		}

		const filter = pageQuery.filterList[filterIndex];

		filter.filterValue = filterChange.col.assignedFilters
			.filter(f => f.value)
			.map(f => new FilterValue({
				operator: f.operator,
				value: f.value
			}));

		if (!filter.filterValue.length) {
			pageQuery.filterList.splice(filterIndex, 1);
		}

		filterChange.col.hasFilterApplied = filterChange.col.assignedFilters
			.some(f => filterChange.col.filterType === FilterType.Basic &&
				(f.value || f.dateValue || this.filtersWithoutValue.includes(f.operator)) ||
				filterChange.col.filterType !== FilterType.Basic && f.isChecked);
	}
}